import { Personnel } from '../../../../shared/types/game';
import { PersonnelConfig, Competence } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { isTerminalInterventionInstanceStatutRaw } from '../../../../shared/constants/interventionInstanceStatus';

/** Données chargées par `loadPersonnelDetails` pour la modale détail personnel. */
export interface PersonnelDetailsLoadResult {
  personnelCompetences: Competence[];
  gradeDefaultCompetences: Competence[];
  posteNom: string | null;
  statutGeneral: string;
  statutPlanning: string;
  derniereIntervention: { date: string; heure: string } | null;
  groupe: string | null | undefined;
}

function formatInterventionDateTime(rawDate: string): { date: string; heure: string } | null {
  try {
    const dateObj = new Date(rawDate);
    if (Number.isNaN(dateObj.getTime())) return null;
    return {
      date: dateObj.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      heure: dateObj.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  } catch {
    return null;
  }
}

/**
 * Charge en parallèle toutes les données affichées dans la modale de détail personnel :
 * compétences, poste, statuts, dernière intervention, groupe planning, et compétences
 * par défaut du grade. Extrait de usePersonnelTab.ts.
 */
export async function loadPersonnelDetails(
  p: Personnel,
  buildingId: number,
  token: string
): Promise<PersonnelDetailsLoadResult> {
  const result: PersonnelDetailsLoadResult = {
    personnelCompetences: [],
    gradeDefaultCompetences: [],
    posteNom: null,
    statutGeneral: (p as any).statut_nom ?? 'DISPONIBLE',
    statutPlanning: (p as any).planning_statut_nom ?? 'PERSONNELS',
    derniereIntervention: null,
    groupe: undefined,
  };

  // 1) Compétences directes du personnel
  try {
    const personnelResponse = await fetch(
      `${getApiBaseUrl(true)}/buildings/personnel/${p.id}/competences`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (personnelResponse.ok) {
      const d = await personnelResponse.json();
      if (d.success && Array.isArray(d.data)) result.personnelCompetences = d.data;
    }
  } catch {
    /* ignore */
  }

  // 2) Nom du poste
  if ((p as any).poste_id && buildingId) {
    try {
      const postesResponse = await fetch(
        `${getApiBaseUrl(true)}/buildings/${buildingId}/postes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (postesResponse.ok) {
        const pd = await postesResponse.json();
        if (pd.success && Array.isArray(pd.data)) {
          const poste = pd.data.find((pt: any) => pt.id === (p as any).poste_id);
          if (poste?.poste_type_nom) result.posteNom = poste.poste_type_nom;
        }
      }
    } catch {
      /* ignore */
    }
  }

  // 3) Statuts depuis l'endpoint /details (rafraîchissement)
  try {
    const detailsResponse = await fetch(
      `${getApiBaseUrl(true)}/buildings/personnel/${p.id}/details`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (detailsResponse.ok) {
      const dd = await detailsResponse.json();
      if (dd.success && dd.data) {
        if (dd.data.statut_nom) result.statutGeneral = dd.data.statut_nom;
        if (dd.data.planning_statut_nom) result.statutPlanning = dd.data.planning_statut_nom;
        if (Object.prototype.hasOwnProperty.call(dd.data, 'groupe')) {
          result.groupe = dd.data.groupe;
        }
      }
    }
  } catch {
    /* endpoint peut ne pas exister */
  }

  // 4) Dernière intervention — champ direct
  if ((p as any).derniere_intervention_date) {
    const fmt = formatInterventionDateTime((p as any).derniere_intervention_date);
    if (fmt) result.derniereIntervention = fmt;
  }

  // 5) Dernière intervention — fallback updated_at quand DISPONIBLE
  if (!result.derniereIntervention && result.statutGeneral === 'DISPONIBLE' && (p as any).updated_at) {
    const fmt = formatInterventionDateTime((p as any).updated_at);
    if (fmt) result.derniereIntervention = fmt;
  }

  // 6) Dernière intervention — recherche dans les interventions
  if (!result.derniereIntervention) {
    try {
      const interventionsResponse = await fetch(
        `${getApiBaseUrl(true)}/buildings/interventions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (interventionsResponse.ok) {
        const interventionsData = await interventionsResponse.json();
        if (interventionsData.success && Array.isArray(interventionsData.data)) {
          const terminees = interventionsData.data.filter((intervention: any) => {
            let ids: number[] = [];
            if (intervention.personnel_ids && Array.isArray(intervention.personnel_ids)) {
              ids = intervention.personnel_ids;
            } else if (intervention.personnels && Array.isArray(intervention.personnels)) {
              ids = intervention.personnels.map((x: any) => (typeof x === 'number' ? x : x.id));
            } else if (intervention.personnel_id) {
              ids = [intervention.personnel_id];
            }
            if (!ids.includes(p.id)) return false;
            const statut = intervention.statut || '';
            const hasDateFin = intervention.date_fin || intervention.date_fin_intervention;
            return isTerminalInterventionInstanceStatutRaw(statut) || hasDateFin;
          });

          terminees.sort((a: any, b: any) => {
            const dA = a.date_fin || a.date_fin_intervention || a.updated_at || a.created_at || '';
            const dB = b.date_fin || b.date_fin_intervention || b.updated_at || b.created_at || '';
            return new Date(dB).getTime() - new Date(dA).getTime();
          });

          if (terminees.length > 0) {
            const last = terminees[0];
            const dateFin = last.date_fin || last.date_fin_intervention || last.updated_at;
            if (dateFin) {
              const fmt = formatInterventionDateTime(dateFin);
              if (fmt) result.derniereIntervention = fmt;
            }
          }
        }
      }
    } catch {
      /* ignore */
    }
  }

  // 7) Compétences par défaut du grade
  try {
    const configResponse = await fetch(`${getApiBaseUrl(true)}/config/personnel`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (configResponse.ok) {
      const configData = await configResponse.json();
      if (configData.success && Array.isArray(configData.data)) {
        const gradeConfig = configData.data.find(
          (cfg: PersonnelConfig) =>
            cfg.grade === p.grade || cfg.id === (p as any).grade_config_id
        );
        if (gradeConfig?.id_competences?.length > 0) {
          const competencesResponse = await fetch(`${getApiBaseUrl(true)}/config/competences`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (competencesResponse.ok) {
            const competencesData = await competencesResponse.json();
            if (competencesData.success && Array.isArray(competencesData.data)) {
              result.gradeDefaultCompetences = competencesData.data.filter((comp: Competence) =>
                gradeConfig.id_competences.includes(comp.id)
              );
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement des compétences:', error);
  }

  return result;
}
